import React from 'react';
import { CheckCircle2Icon, ClockIcon, OctagonAlertIcon, ShieldAlertIcon } from 'lucide-react';
import type { Monitor } from '../types/monitor';
import { Button } from './ui/Button';
import { findTopConcern } from '../utils/health';
import { relativeTime } from '../utils/format';
interface AttentionPanelProps {
  monitors: Monitor[];
}
export function AttentionPanel({
  monitors
}: AttentionPanelProps) {
  const concern = findTopConcern(monitors);
  if (!concern) {
    const next = monitors.map(monitor => monitor.nextCheckAt).sort().shift();
    return <section aria-label="Current status" className="flex flex-wrap items-center gap-3 rounded-card border border-healthy-border bg-healthy-soft px-5 py-4" data-id="element-141" data-mp="130u6aybp582q">
        <CheckCircle2Icon className="h-5 w-5 shrink-0 text-healthy" aria-hidden="true" data-id="element-142" data-mp="u0fls16k3x0a" />
        <div className="min-w-0" data-id="element-143" data-mp="p1kppxjkwby8">
          <h2 className="text-sm font-semibold text-healthy-ink" data-id="element-144" data-mp="p2nh06ktcgeo">
            Nothing needs you right now
          </h2>
          <p className="mt-0.5 text-[13px] text-healthy-ink/85" data-id="element-145" data-mp="3uxruldnqbrvo">
            Every site is responding and no certificate is inside its warning
            window.
            {next && ` Next check ${relativeTime(next)}.`}
          </p>
        </div>
      </section>;
  }
  const {
    monitor,
    kind,
    days
  } = concern;
  const critical = kind === 'down' || kind === 'expired' || kind === 'unreadable';
  const headline = kind === 'down' ? `${monitor.domain} is not responding` : kind === 'expired' ? `${monitor.domain} has an expired certificate` : kind === 'unreadable' ? `We cannot read the certificate on ${monitor.domain}` : `${monitor.domain} expires in ${days} days`;
  const detail = kind === 'down' ? `${monitor.failureThreshold} consecutive checks failed. ${monitor.incidents[0]?.reason ?? 'No response from the server.'} Recipients were emailed when it started.` : kind === 'expired' ? `The certificate lapsed ${Math.abs(days ?? 0)} days ago, so visitors are seeing a browser warning right now.` : kind === 'unreadable' ? monitor.certificateError ?? 'The last inspection returned no certificate.' : `Renew before ${monitor.certificate ? new Date(monitor.certificate.validTo).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long'
  }) : 'the expiry date'}. Issued by ${monitor.certificate?.issuer ?? 'unknown issuer'}.`;
  return <section aria-label="Needs attention" className={`rounded-card border-l-4 px-5 py-5 ${critical ? 'border border-l-critical border-critical-border bg-critical-soft' : 'border border-l-warn border-warn-border bg-warn-soft'}`} data-id="element-146" data-mp="72ciz29w5h15">
      <div className="flex items-start gap-3" data-id="element-147" data-mp="21e9byznzeuyb">
        {critical ? <OctagonAlertIcon className="mt-0.5 h-5 w-5 shrink-0 text-critical" aria-hidden="true" data-id="element-148" data-mp="3jdh5fani3w2y" /> : <ShieldAlertIcon className="mt-0.5 h-5 w-5 shrink-0 text-warn" aria-hidden="true" data-id="element-149" data-mp="3t3sch62hele2" />}
        <div className="min-w-0 flex-1" data-id="element-150" data-mp="yy5gylmks3j">
          <h2 className={`text-lg font-semibold leading-snug ${critical ? 'text-critical-ink' : 'text-warn-ink'}`} data-id="element-151" data-mp="123xalln282y">
            {headline}
          </h2>
          <p className={`mt-1.5 max-w-2xl text-[13px] leading-relaxed ${critical ? 'text-critical-ink/85' : 'text-warn-ink/85'}`} data-id="element-152" data-mp="35g2ajqehaw3l">
            {detail}
          </p>
          <p className={`mt-2 flex items-center gap-1.5 text-xs ${critical ? 'text-critical-ink/75' : 'text-warn-ink/75'}`} data-id="element-153" data-mp="opu3idgocjj8">
            <ClockIcon className="h-3 w-3" aria-hidden="true" data-id="element-154" data-mp="tqhu4y0zqycx" />
            Last checked {relativeTime(monitor.lastCheckedAt)} · next{' '}
            {relativeTime(monitor.nextCheckAt)}
          </p>

          <div className="mt-4 flex flex-wrap gap-2" data-id="element-155" data-mp="nardpga90c48">
            <Button size="sm" to={`/app/monitors/${monitor.id}`} data-id="element-156" data-mp="119911cw8rmlm">
              Open {monitor.domain}
            </Button>
            <Button size="sm" variant="secondary" to="/app/alerts" data-id="element-157" data-mp="x5ax8ovz1nm8">
              See what we sent
            </Button>
          </div>
        </div>
      </div>
    </section>;
}